// Ideas API service

const API_BASE_URL = () => {
  // Check if we're in production
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://findashback.onrender.com';
  }
  
  // Development environment
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export interface Idea {
  _id?: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  priority: 'High' | 'Medium' | 'Low';
  status: 'New' | 'In Progress' | 'Researching' | 'On Hold' | 'Completed';
  isFavorite?: boolean;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IdeaStats {
  total: number;
  inProgress: number;
  highPriority: number;
  favorites: number;
  completed: number;
}

export interface IdeaFilters {
  category?: string;
  status?: string;
  priority?: string;
  search?: string;
}

export const ideasApi = {
  // Fetch all ideas with optional filters
  async fetchIdeas(filters?: IdeaFilters): Promise<Idea[]> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.priority) queryParams.append('priority', filters.priority);
      if (filters?.search) queryParams.append('search', filters.search);
      
      const url = `${API_BASE_URL()}/ideas${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching ideas:', error);
      throw error;
    }
  },

  // Create a new idea
  async createIdea(ideaData: Omit<Idea, '_id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<Idea> {
    try {
      const response = await fetch(`${API_BASE_URL()}/ideas`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(ideaData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating idea:', error);
      throw error;
    }
  },

  // Update an existing idea
  async updateIdea(ideaId: string, updates: Partial<Idea>): Promise<Idea> {
    try {
      const response = await fetch(`${API_BASE_URL()}/ideas/${ideaId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating idea:', error);
      throw error;
    }
  },

  // Toggle favorite status
  async toggleFavorite(ideaId: string): Promise<Idea> {
    try {
      const response = await fetch(`${API_BASE_URL()}/ideas/${ideaId}/favorite`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  },

  // Delete an idea
  async deleteIdea(ideaId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL()}/ideas/${ideaId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting idea:', error);
      throw error;
    }
  },

  // Delete an idea and associated projects
  async deleteIdeaWithProjects(ideaId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL()}/ideas/${ideaId}/with-projects`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting idea & projects:', error);
      throw error;
    }
  },

  // Delete idea, projects, and all related tasks
  async deleteIdeaWithEverything(ideaId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL()}/ideas/${ideaId}/with-everything`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting everything:', error);
      throw error;
    }
  },

  // Get idea statistics
  async getStats(): Promise<IdeaStats> {
    try {
      const response = await fetch(`${API_BASE_URL()}/ideas/stats`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching idea stats:', error);
      throw error;
    }
  },

  // Get unique categories
  async getCategories(): Promise<string[]> {
    try {
      const response = await fetch(`${API_BASE_URL()}/ideas/categories`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }
};

export interface ProjectStatus {
  ideaId: string;
  hasProject: boolean;
  projectId: string | null;
  projectName: string | null;
}

export const checkIdeaProjectStatus = async (ideaId: string): Promise<ProjectStatus> => {
  try {
    const response = await fetch(`${API_BASE_URL()}/ideas/${ideaId}/project-status`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to check project status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking project status:', error);
    throw error;
  }
};

// Batch check project status for multiple ideas
export const checkMultipleIdeaProjectStatus = async (ideaIds: string[]): Promise<Record<string, ProjectStatus>> => {
  const results: Record<string, ProjectStatus> = {};
  
  // Use Promise.all for parallel requests
  const statusPromises = ideaIds.map(async (ideaId) => {
    try {
      const status = await checkIdeaProjectStatus(ideaId);
      return { ideaId, status };
    } catch (error) {
      console.error(`Failed to check status for idea ${ideaId}:`, error);
      return { 
        ideaId, 
        status: { 
          ideaId, 
          hasProject: false, 
          projectId: null, 
          projectName: null 
        } 
      };
    }
  });

  const statusResults = await Promise.all(statusPromises);
  statusResults.forEach(({ ideaId, status }) => {
    results[ideaId] = status;
  });

  return results;
};
